import axios from '../axiosConfig';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost/api';

function normalizeText(value, fallback = '') {
  if (Array.isArray(value)) {
    const flattened = value
      .flatMap((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (Array.isArray(item)) {
          return item.map((nested) => normalizeText(nested, '')).filter(Boolean);
        }
        if (item && typeof item === 'object') {
          return Object.values(item).map((nested) => normalizeText(nested, '')).filter(Boolean);
        }
        return [];
      })
      .filter(Boolean)
      .join(' ');

    return flattened.trim() || fallback;
  }

  if (value && typeof value === 'object') {
    const flattened = Object.values(value)
      .map((item) => normalizeText(item, ''))
      .filter(Boolean)
      .join(' ');

    return flattened.trim() || fallback;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized || normalized === 'Array' || normalized === '[object Object]') {
      return fallback;
    }
    return normalized;
  }

  return fallback;
}

function normalizeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function clampRiskLevel(value, fallback = 'moderate') {
  return value === 'high' || value === 'moderate' ? value : fallback;
}

function inferDefaultPassMark(subject) {
  const normalized = String(subject || '').trim().toLowerCase();
  return normalized.includes('home language') || normalized.includes('huistaal') ? 50 : 40;
}

function buildWeakObservation(averagePercentage) {
  if (averagePercentage < 40) {
    return 'Learners are showing a serious gap here and need immediate reteaching before the next formal task.';
  }

  if (averagePercentage < 60) {
    return 'Learners show partial understanding, but accuracy and independent application still need strengthening.';
  }

  return 'This area is more secure than the weakest topics, but it still needs short-cycle consolidation.';
}

function buildQuestionType(topic) {
  const normalized = String(topic || '').toLowerCase();
  if (normalized.includes('problem') || normalized.includes('application') || normalized.includes('word')) {
    return 'Application and reasoning';
  }
  if (normalized.includes('graph') || normalized.includes('diagram') || normalized.includes('table')) {
    return 'Visual interpretation';
  }
  if (normalized.includes('essay') || normalized.includes('paragraph') || normalized.includes('writing')) {
    return 'Extended response';
  }
  return 'Short response and procedural recall';
}

function buildDiagnosticsFallbackData(payload) {
  const students = Array.isArray(payload?.students) ? payload.students : [];
  const passRequirements = payload?.passRequirements ?? {};
  const passMark = Number.isFinite(Number(passRequirements?.pass_mark))
    ? Number(passRequirements.pass_mark)
    : inferDefaultPassMark(payload?.subject);
  const supportThreshold = Number.isFinite(Number(passRequirements?.support_threshold))
    ? Number(passRequirements.support_threshold)
    : Math.max(passMark, 55);
  const distinctionThreshold = Number.isFinite(Number(passRequirements?.distinction_threshold))
    ? Number(passRequirements.distinction_threshold)
    : 70;

  const overallPercentages = [];
  const topicBuckets = new Map();

  students.forEach((student) => {
    const overallPercentage = Number(student?.overallPercentage);
    if (Number.isFinite(overallPercentage)) {
      overallPercentages.push(overallPercentage);
    }

    const topicMarks = Array.isArray(student?.topicMarks) ? student.topicMarks : [];
    topicMarks.forEach((topicMark) => {
      const topic = normalizeText(topicMark?.topic, 'Assessment');
      const score = normalizeNumber(topicMark?.score, 0);
      const total = normalizeNumber(topicMark?.total, 0);
      const percentage = total > 0 ? (score / total) * 100 : 0;

      if (!topicBuckets.has(topic)) {
        topicBuckets.set(topic, []);
      }

      topicBuckets.get(topic).push(percentage);
    });
  });

  const weakAreas = Array.from(topicBuckets.entries())
    .map(([topic, percentages]) => {
      const averagePercentage = percentages.length
        ? percentages.reduce((sum, value) => sum + value, 0) / percentages.length
        : 0;
      const severity = averagePercentage < 40 ? 'high' : averagePercentage < 60 ? 'medium' : 'low';

      return {
        topic,
        averagePercentage: Number(averagePercentage.toFixed(2)),
        severity,
        observation: buildWeakObservation(averagePercentage),
        questionType: buildQuestionType(topic),
        commonErrors: averagePercentage < 40
          ? [
              `Learners struggle to identify what ${topic} is assessing.`,
              'Key steps are missing or completed in the wrong order.',
              'Answers are often given without a clear method or justification.'
            ]
          : [
              `Learners start ${topic} correctly but lose marks in the middle steps.`,
              'Accuracy drops when the task becomes more complex.',
              'Learners need more practice transferring the method to similar questions.'
            ],
        recommendedFocus: averagePercentage < 40
          ? `Reteach the core method and vocabulary for ${topic}, then move from scaffolded examples to short independent practice.`
          : `Use corrections and focused follow-up practice on ${topic} so learners can apply the method with less prompting.`
      };
    })
    .sort((a, b) => a.averagePercentage - b.averagePercentage);

  const strengthAreas = [...weakAreas]
    .sort((a, b) => b.averagePercentage - a.averagePercentage)
    .slice(0, 3)
    .map((area) => ({
      topic: area.topic,
      averagePercentage: area.averagePercentage,
      insight: area.averagePercentage >= 75
        ? 'This is a relative strength and can be used for peer support and extension work.'
        : 'This area is stronger than the rest of the assessment and can anchor mixed revision.'
    }));

  const learnerCount = students.length;
  const classAverage = overallPercentages.length
    ? Number((overallPercentages.reduce((sum, value) => sum + value, 0) / overallPercentages.length).toFixed(2))
    : 0;
  const below50Count = overallPercentages.filter((value) => value < 50).length;
  const passCount = overallPercentages.filter((value) => value >= passMark).length;
  const atOrAbove70Count = overallPercentages.filter((value) => value >= distinctionThreshold).length;
  const passRate = overallPercentages.length ? Number(((passCount / overallPercentages.length) * 100).toFixed(2)) : 0;

  const atRiskStudents = [...students]
    .map((student) => ({
      studentName: normalizeText(student?.studentName, 'Learner'),
      overallPercentage: Number(student?.overallPercentage)
    }))
    .filter((student) => Number.isFinite(student.overallPercentage) && student.overallPercentage <= supportThreshold)
    .sort((a, b) => a.overallPercentage - b.overallPercentage)
    .slice(0, 8)
    .map((student) => ({
      studentName: student.studentName,
      riskLevel: student.overallPercentage < passMark ? 'high' : 'moderate',
      reason: student.overallPercentage < passMark
        ? `Overall percentage is below the active pass mark of ${passMark}%, indicating a significant support need.`
        : `Overall percentage is at or below the support threshold of ${supportThreshold}%, showing a borderline support need.`,
      supportPriority: student.overallPercentage < passMark ? 'Urgent support this week.' : 'Targeted monitoring and guided support.',
      recommendedAction: student.overallPercentage < passMark
        ? 'Provide focused reteaching, scaffolded classwork, and a short follow-up check within the week.'
        : 'Use guided correction, paired support, and a quick reassessment after revision.'
    }));

  const suggestedInterventions = weakAreas.slice(0, 3).map((area) => ({
    title: `Focused intervention on ${area.topic}`,
    action: area.severity === 'high'
      ? 'Reteach the concept explicitly, model worked examples, and then move learners into scaffolded practice before independent application.'
      : 'Use targeted revision, oral questioning, and short correction tasks to strengthen accuracy and explanation.',
    targetTopics: [area.topic],
    timeframe: area.severity === 'high' ? 'Within the next 1-2 lessons.' : 'During the next revision cycle this week.',
    suitableFor: area.severity === 'high' ? 'Whole-class reteach followed by a support group.' : 'Focused support group and consolidation.'
  }));

  return {
    summary: weakAreas.length
      ? `The analysis shows the strongest remediation need in ${weakAreas.slice(0, 3).map((item) => item.topic).join(', ')}.`
      : 'The available assessment data suggests a short consolidation review is still needed.',
    headlineFindings: [
      learnerCount
        ? `The class average is ${classAverage}% across ${learnerCount} learner${learnerCount === 1 ? '' : 's'}.`
        : 'No learner records were available for analysis.',
      `${below50Count} learner${below50Count === 1 ? '' : 's'} are below 50%, while ${atOrAbove70Count} are already at 70% or above.`,
      weakAreas[0]
        ? `The most urgent reteaching need is ${weakAreas[0].topic} at ${Math.round(weakAreas[0].averagePercentage)}%.`
        : 'No single weak area stood out strongly from the available assessment data.'
    ],
    cohortSnapshot: {
      learnerCount,
      classAverage,
      passRate,
      below50Count,
      atOrAbove70Count
    },
    performanceBands: [
      {
        label: '0-39%',
        learnerCount: overallPercentages.filter((value) => value < 40).length,
        description: 'Learners in this band need intensive support and close monitoring.'
      },
      {
        label: '40-69%',
        learnerCount: overallPercentages.filter((value) => value >= 40 && value < 70).length,
        description: 'Learners in this band need consolidation and more consistent accuracy.'
      },
      {
        label: '70-100%',
        learnerCount: atOrAbove70Count,
        description: 'Learners in this band can extend learning and support peers.'
      }
    ],
    weakAreas,
    strengthAreas,
    atRiskStudents,
    misconceptionPatterns: weakAreas.slice(0, 3).map((area) => ({
      pattern: `Weak performance in ${area.topic}`,
      evidence: `Average score in this area is ${Math.round(area.averagePercentage)}%.`,
      likelyCause: area.severity === 'high'
        ? 'Learners likely lack the foundational method or vocabulary needed to interpret the task correctly.'
        : 'Learners appear to understand parts of the task but struggle with full independent application.',
      recommendedResponse: area.recommendedFocus
    })),
    suggestedInterventions: suggestedInterventions.length ? suggestedInterventions : [{
      title: 'Consolidation review',
      action: 'Run a mixed revision lesson with retrieval practice, short reteaching, guided examples, and a mini-check at the end.',
      targetTopics: [],
      timeframe: 'Next available lesson.',
      suitableFor: 'Whole class.'
    }],
    assessmentReadiness: {
      status: classAverage >= Math.max(passMark + 10, 60)
        ? 'Ready with targeted support.'
        : 'Not yet secure enough without remediation.',
      rationale: `Class average is ${classAverage}% with ${below50Count} learner${below50Count === 1 ? '' : 's'} below 50%.`,
      nextSteps: [
        'Reteach the weakest topics before the next graded task.',
        'Use a short correction activity and keep evidence of improvement.',
        'Monitor the at-risk group separately over the next 2 weeks.'
      ]
    },
    hodView: {
      monitoringPoints: [
        'Track whether the weakest topics improve in the next class-based check.',
        'Monitor whether the below-50% group reduces after intervention.',
        'Review correction evidence, scripts, and support records.'
      ],
      resourcePriorities: [
        'Provide focused worksheets or exemplars for the weakest topics.',
        'Support small-group intervention time for learners below the support threshold.',
        'Use structured peer support where appropriate.'
      ],
      reportingNote: 'This summary can support subject meetings, HOD monitoring, and short-cycle intervention planning.'
    },
    capsReferences: Array.isArray(payload?.capsReferences) ? payload.capsReferences : []
  };
}

function mergeDiagnosticsResponse(response, payload) {
  const fallback = buildDiagnosticsFallbackData(payload);
  const normalized = isPlainObject(response) ? response : {};

  const mergeObjectArray = (items, defaults, mapper) => {
    if (!Array.isArray(items) || items.length === 0) {
      return defaults;
    }

    return items.map((item, index) => mapper(isPlainObject(item) ? item : {}, defaults[index] || defaults[0] || {}));
  };

  return {
    summary: normalizeText(normalized.summary, fallback.summary),
    headlineFindings: Array.isArray(normalized.headlineFindings) && normalized.headlineFindings.length
      ? normalized.headlineFindings.map((item, index) => normalizeText(item, fallback.headlineFindings[index] || fallback.headlineFindings[0] || ''))
      : fallback.headlineFindings,
    cohortSnapshot: {
      learnerCount: normalizeNumber(normalized.cohortSnapshot?.learnerCount, fallback.cohortSnapshot.learnerCount),
      classAverage: normalizeNumber(normalized.cohortSnapshot?.classAverage, fallback.cohortSnapshot.classAverage),
      passRate: normalizeNumber(normalized.cohortSnapshot?.passRate, fallback.cohortSnapshot.passRate),
      below50Count: normalizeNumber(normalized.cohortSnapshot?.below50Count, fallback.cohortSnapshot.below50Count),
      atOrAbove70Count: normalizeNumber(normalized.cohortSnapshot?.atOrAbove70Count, fallback.cohortSnapshot.atOrAbove70Count)
    },
    performanceBands: mergeObjectArray(normalized.performanceBands, fallback.performanceBands, (item, defaults) => ({
      label: normalizeText(item.label, defaults.label),
      learnerCount: normalizeNumber(item.learnerCount, defaults.learnerCount),
      description: normalizeText(item.description, defaults.description)
    })),
    weakAreas: mergeObjectArray(normalized.weakAreas, fallback.weakAreas, (item, defaults) => ({
      topic: normalizeText(item.topic, defaults.topic),
      averagePercentage: normalizeNumber(item.averagePercentage, defaults.averagePercentage),
      severity: item.severity === 'high' || item.severity === 'medium' || item.severity === 'low' ? item.severity : defaults.severity,
      observation: normalizeText(item.observation, defaults.observation),
      questionType: normalizeText(item.questionType, defaults.questionType),
      commonErrors: Array.isArray(item.commonErrors) && item.commonErrors.length
        ? item.commonErrors.map((entry, index) => normalizeText(entry, defaults.commonErrors?.[index] || defaults.commonErrors?.[0] || ''))
        : defaults.commonErrors,
      recommendedFocus: normalizeText(item.recommendedFocus, defaults.recommendedFocus)
    })),
    strengthAreas: mergeObjectArray(normalized.strengthAreas, fallback.strengthAreas, (item, defaults) => ({
      topic: normalizeText(item.topic, defaults.topic),
      averagePercentage: normalizeNumber(item.averagePercentage, defaults.averagePercentage),
      insight: normalizeText(item.insight, defaults.insight)
    })),
    atRiskStudents: mergeObjectArray(normalized.atRiskStudents, fallback.atRiskStudents, (item, defaults) => ({
      studentName: normalizeText(item.studentName, defaults.studentName),
      riskLevel: clampRiskLevel(item.riskLevel, defaults.riskLevel),
      reason: normalizeText(item.reason, defaults.reason),
      supportPriority: normalizeText(item.supportPriority, defaults.supportPriority),
      recommendedAction: normalizeText(item.recommendedAction, defaults.recommendedAction)
    })),
    misconceptionPatterns: mergeObjectArray(normalized.misconceptionPatterns, fallback.misconceptionPatterns, (item, defaults) => ({
      pattern: normalizeText(item.pattern, defaults.pattern),
      evidence: normalizeText(item.evidence, defaults.evidence),
      likelyCause: normalizeText(item.likelyCause, defaults.likelyCause),
      recommendedResponse: normalizeText(item.recommendedResponse, defaults.recommendedResponse)
    })),
    suggestedInterventions: mergeObjectArray(normalized.suggestedInterventions, fallback.suggestedInterventions, (item, defaults) => ({
      title: normalizeText(item.title, defaults.title),
      action: normalizeText(item.action, defaults.action),
      targetTopics: Array.isArray(item.targetTopics) && item.targetTopics.length
        ? item.targetTopics.map((entry, index) => normalizeText(entry, defaults.targetTopics?.[index] || defaults.targetTopics?.[0] || ''))
        : defaults.targetTopics,
      timeframe: normalizeText(item.timeframe, defaults.timeframe),
      suitableFor: normalizeText(item.suitableFor, defaults.suitableFor)
    })),
    assessmentReadiness: {
      status: normalizeText(normalized.assessmentReadiness?.status, fallback.assessmentReadiness.status),
      rationale: normalizeText(normalized.assessmentReadiness?.rationale, fallback.assessmentReadiness.rationale),
      nextSteps: Array.isArray(normalized.assessmentReadiness?.nextSteps) && normalized.assessmentReadiness.nextSteps.length
        ? normalized.assessmentReadiness.nextSteps.map((item, index) => normalizeText(item, fallback.assessmentReadiness.nextSteps[index] || fallback.assessmentReadiness.nextSteps[0] || ''))
        : fallback.assessmentReadiness.nextSteps
    },
    hodView: {
      monitoringPoints: Array.isArray(normalized.hodView?.monitoringPoints) && normalized.hodView.monitoringPoints.length
        ? normalized.hodView.monitoringPoints.map((item, index) => normalizeText(item, fallback.hodView.monitoringPoints[index] || fallback.hodView.monitoringPoints[0] || ''))
        : fallback.hodView.monitoringPoints,
      resourcePriorities: Array.isArray(normalized.hodView?.resourcePriorities) && normalized.hodView.resourcePriorities.length
        ? normalized.hodView.resourcePriorities.map((item, index) => normalizeText(item, fallback.hodView.resourcePriorities[index] || fallback.hodView.resourcePriorities[0] || ''))
        : fallback.hodView.resourcePriorities,
      reportingNote: normalizeText(normalized.hodView?.reportingNote, fallback.hodView.reportingNote)
    },
    capsReferences: Array.isArray(normalized.capsReferences) && normalized.capsReferences.length
      ? normalized.capsReferences.map((item) => ({
          title: normalizeText(item?.title, 'Official CAPS reference'),
          url: normalizeText(item?.url, ''),
          relevance: normalizeText(item?.relevance, 'Official CAPS source used for alignment.')
        }))
      : fallback.capsReferences
  };
}

function buildRevisionFallbackData(payload) {
  const weakTopics = Array.isArray(payload?.weakTopics) && payload.weakTopics.length
    ? payload.weakTopics
    : ['Core concepts review'];
  const requestedDays = Math.max(1, Number(payload?.days) || weakTopics.length || 1);
  const days = Array.from({ length: requestedDays }, (_, index) => {
    const focusTopic = normalizeText(weakTopics[index % weakTopics.length], 'Core concepts review');
    return {
      day: index + 1,
      focusTopic,
      objective: `By the end of this lesson, learners should apply ${focusTopic} more accurately in guided and independent CAPS-style tasks.`,
      capsFocus: `CAPS focus for ${normalizeText(payload?.subject, 'the subject')} Grade ${normalizeText(payload?.grade, 'the grade')}: strengthen understanding, vocabulary, method, and application in ${focusTopic}.`,
      starter: `Begin with a short retrieval activity on ${focusTopic}, using prior classwork, oral questions, or a quick board task.`,
      teacherInput: `Reteach ${focusTopic} step by step, model the method aloud, and highlight common errors from the diagnostic.`,
      activities: [
        `Teacher-led worked example on ${focusTopic}, with learners explaining the steps in pairs.`,
        `Guided practice in pairs or small groups using scaffolded questions that move from recall to application.`,
        `Independent CAPS-style practice on ${focusTopic}, followed by memo discussion and corrections.`
      ],
      differentiation: 'Provide scaffolded support, worked examples, teacher check-ins, and extension questions according to learner need.',
      resources: `Use classwork books, board work, teacher memo, short revision worksheets, and CAPS-approved examples linked to ${focusTopic}.`,
      homework: `Complete a short revision task on ${focusTopic} and revise the key method or vocabulary before the next lesson.`,
      informalAssessment: `Monitor oral responses and written work to check whether learners can complete ${focusTopic} tasks more independently.`,
      exitTicket: `Answer the three short check questions on ${focusTopic}; use the memo to identify the next reteaching step.`,
      teacherQuestions: [
        `What is the first step or key idea you use when working with ${focusTopic}?`,
        `How can you check that your answer or explanation about ${focusTopic} makes sense?`
      ],
      assessmentItems: [
        {
          question: `In your own words, state one important rule, fact, or step you have learnt about ${focusTopic}.`,
          memo: `Award 2 marks for a correct grade-appropriate rule, fact, or step from the teacher's current work on ${focusTopic}; award 1 mark for a partly correct idea.`,
          marks: 2,
          cognitiveLevel: 'Knowledge and understanding'
        },
        {
          question: `Explain how you would use ${focusTopic} to complete a familiar classwork task. Write the steps in the correct order.`,
          memo: 'Award 1 mark for each correct, relevant step, up to 3 marks. Accept equivalent grade-appropriate wording.',
          marks: 3,
          cognitiveLevel: 'Routine procedure'
        },
        {
          question: `A learner makes a mistake while working with ${focusTopic}. Describe one mistake they could make and explain how to correct it.`,
          memo: 'Award 1 mark for a plausible topic-related error and 1 mark for a correct correction or explanation.',
          marks: 2,
          cognitiveLevel: 'Application and reasoning'
        }
      ]
    };
  });

  return {
    planTitle: `${normalizeText(payload?.subject, 'Subject')} CAPS-Aligned Revision Plan`,
    overview: {
      grade: normalizeText(payload?.grade, 'Grade not specified'),
      subject: normalizeText(payload?.subject, 'Subject'),
      days: requestedDays,
      capsAlignment: 'The plan uses a CAPS-style remediation flow: retrieval, explicit reteaching, guided practice, independent application, and informal assessment.',
      purpose: 'Use this plan to close learning gaps from the selected assessment and prepare learners for improved performance in follow-up classwork and formal tasks.'
    },
    days,
    successMetrics: [
      'More learners complete the exit ticket correctly without heavy prompting.',
      'Fewer repeated errors appear in classwork, corrections, and follow-up assessments.',
      'Learners show stronger method, vocabulary, and confidence in the targeted topics.'
    ],
    teacherMoves: [
      'Keep a visible error log from the diagnostic and refer to it during each reteach session.',
      'Group learners flexibly based on the weak-topic pattern.',
      'End each lesson with written evidence that can be tracked over time.'
    ],
    capsReferences: Array.isArray(payload?.capsReferences) ? payload.capsReferences : []
  };
}

function mergeRevisionPlanResponse(response, payload) {
  const fallback = buildRevisionFallbackData(payload);
  const normalized = isPlainObject(response) ? response : {};
  const fallbackDays = fallback.days;

  return {
    planTitle: normalizeText(normalized.planTitle, fallback.planTitle),
    overview: {
      grade: normalizeText(normalized.overview?.grade, fallback.overview.grade),
      subject: normalizeText(normalized.overview?.subject, fallback.overview.subject),
      days: normalizeNumber(normalized.overview?.days, fallback.overview.days),
      capsAlignment: normalizeText(normalized.overview?.capsAlignment, fallback.overview.capsAlignment),
      purpose: normalizeText(normalized.overview?.purpose, fallback.overview.purpose)
    },
    days: Array.isArray(normalized.days) && normalized.days.length
      ? normalized.days.map((day, index) => {
          const defaults = fallbackDays[index] || fallbackDays[fallbackDays.length - 1];
          return {
            day: normalizeNumber(day?.day, defaults.day),
            focusTopic: normalizeText(day?.focusTopic, defaults.focusTopic),
            objective: normalizeText(day?.objective, defaults.objective),
            capsFocus: normalizeText(day?.capsFocus, defaults.capsFocus),
            starter: normalizeText(day?.starter, defaults.starter),
            teacherInput: normalizeText(day?.teacherInput, defaults.teacherInput),
            activities: Array.isArray(day?.activities) && day.activities.length
              ? day.activities.map((item, itemIndex) => normalizeText(item, defaults.activities[itemIndex] || defaults.activities[0]))
              : defaults.activities,
            differentiation: normalizeText(day?.differentiation, defaults.differentiation),
            resources: normalizeText(day?.resources, defaults.resources),
            homework: normalizeText(day?.homework, defaults.homework),
            informalAssessment: normalizeText(day?.informalAssessment, defaults.informalAssessment),
            exitTicket: normalizeText(day?.exitTicket, defaults.exitTicket),
            teacherQuestions: Array.isArray(day?.teacherQuestions) && day.teacherQuestions.length
              ? day.teacherQuestions.map((item, itemIndex) => normalizeText(item, defaults.teacherQuestions[itemIndex] || defaults.teacherQuestions[0]))
              : defaults.teacherQuestions,
            assessmentItems: Array.isArray(day?.assessmentItems) && day.assessmentItems.length
              ? day.assessmentItems.map((item, itemIndex) => {
                  const itemDefaults = defaults.assessmentItems[itemIndex] || defaults.assessmentItems[0];
                  return {
                    question: normalizeText(item?.question, itemDefaults.question),
                    memo: normalizeText(item?.memo, itemDefaults.memo),
                    marks: normalizeNumber(item?.marks, itemDefaults.marks),
                    cognitiveLevel: normalizeText(item?.cognitiveLevel, itemDefaults.cognitiveLevel)
                  };
                })
              : defaults.assessmentItems
          };
        })
      : fallback.days,
    successMetrics: Array.isArray(normalized.successMetrics) && normalized.successMetrics.length
      ? normalized.successMetrics.map((item, index) => normalizeText(item, fallback.successMetrics[index] || fallback.successMetrics[0]))
      : fallback.successMetrics,
    teacherMoves: Array.isArray(normalized.teacherMoves) && normalized.teacherMoves.length
      ? normalized.teacherMoves.map((item, index) => normalizeText(item, fallback.teacherMoves[index] || fallback.teacherMoves[0]))
      : fallback.teacherMoves,
    capsReferences: Array.isArray(normalized.capsReferences) && normalized.capsReferences.length
      ? normalized.capsReferences.map((item) => ({
          title: normalizeText(item?.title, 'Official CAPS reference'),
          url: normalizeText(item?.url, ''),
          relevance: normalizeText(item?.relevance, 'Official CAPS source used for alignment.')
        }))
      : fallback.capsReferences
  };
}

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function tryParseJsonString(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }

  const withoutFences = trimmed
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();

  try {
    return JSON.parse(withoutFences);
  } catch (error) {
    return value;
  }
}

function unwrapStructuredPayload(value, keys) {
  const parsedValue = tryParseJsonString(value);

  if (isPlainObject(parsedValue) && keys.every((key) => parsedValue[key] !== undefined)) {
    return parsedValue;
  }

  const nestedCandidates = isPlainObject(parsedValue)
    ? [
        parsedValue.data,
        parsedValue.result,
        parsedValue.response,
        parsedValue.payload,
        parsedValue.rubric,
        parsedValue.diagnostics,
        parsedValue.plan,
        parsedValue.content
      ]
    : [];

  for (const candidate of nestedCandidates) {
    const parsedCandidate = tryParseJsonString(candidate);
    if (isPlainObject(parsedCandidate) && keys.every((key) => parsedCandidate[key] !== undefined)) {
      return parsedCandidate;
    }
  }

  return parsedValue;
}

export function normalizeAiError(error) {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    if (error.code === 'ECONNABORTED') {
      return 'The AI request timed out. Please try again.';
    }
  }

  return error instanceof Error ? error.message : 'Something went wrong while contacting the AI service.';
}

function validateRubricResponse(value) {
  const normalized = unwrapStructuredPayload(value, ['title', 'criteria']);

  if (!isPlainObject(normalized) || !Array.isArray(normalized.criteria) || typeof normalized.title !== 'string') {
    console.error('Unexpected rubric payload:', value);
    throw new Error('Rubric response was not in the expected format.');
  }

  return normalized;
}

function validateDiagnosticsResponse(value) {
  const normalized = unwrapStructuredPayload(value, ['summary', 'weakAreas']);

  if (!isPlainObject(normalized) || !Array.isArray(normalized.weakAreas) || typeof normalized.summary !== 'string') {
    console.error('Unexpected diagnostics payload:', value);
    throw new Error('Diagnostics response was not in the expected format.');
  }

  return normalized;
}

function validateRevisionPlanResponse(value) {
  const normalized = unwrapStructuredPayload(value, ['planTitle', 'days']);

  if (!isPlainObject(normalized) || !Array.isArray(normalized.days) || typeof normalized.planTitle !== 'string') {
    console.error('Unexpected revision plan payload:', value);
    throw new Error('Revision plan response was not in the expected format.');
  }

  return normalized;
}

function validateLessonPlanResponse(value) {
  const normalized = unwrapStructuredPayload(value, ['schoolName', 'lessons', 'classes']);

  if (
    !isPlainObject(normalized) ||
    typeof normalized.schoolName !== 'string' ||
    !Array.isArray(normalized.lessons) ||
    !Array.isArray(normalized.classes)
  ) {
    console.error('Unexpected lesson plan payload:', value);
    throw new Error('Lesson plan response was not in the expected format.');
  }

  return normalized;
}

export async function generateRubric(payload) {
  if (!payload.grade.trim() || !payload.subject.trim() || !payload.taskType.trim() || payload.totalMarks <= 0) {
    throw new Error('Grade, subject, task type, and total marks are required.');
  }

  const response = await axios.post(`${API_BASE}/ai/rubric.php`, {
    ...payload,
    additionalContext: typeof payload.additionalContext === 'string' ? payload.additionalContext.trim() : ''
  }, {
    timeout: 70000
  });

  return validateRubricResponse(response.data);
}

export async function runDiagnostics(payload) {
  if (!payload.subject.trim() || payload.students.length === 0) {
    throw new Error('A subject and at least one student record are required.');
  }

  const response = await axios.post(`${API_BASE}/ai/diagnostics.php`, {
    ...payload,
    additionalContext: typeof payload.additionalContext === 'string' ? payload.additionalContext.trim() : ''
  }, {
    timeout: 70000
  });

  if (isPlainObject(response.data) && typeof response.data.error === 'string' && response.data.error.trim()) {
    throw new Error(response.data.error);
  }

  try {
    return mergeDiagnosticsResponse(validateDiagnosticsResponse(response.data), payload);
  } catch (error) {
    // Keep diagnostics usable when an AI provider returns a malformed success payload.
    console.warn('Using local diagnostics fallback because the AI response was malformed.', response.data);
    return buildDiagnosticsFallbackData(payload);
  }
}

export async function generateRevisionPlan(payload) {
  if (!payload.subject.trim() || payload.weakTopics.length === 0) {
    throw new Error('A subject and at least one weak topic are required.');
  }

  const response = await axios.post(`${API_BASE}/ai/revision-plan.php`, payload, {
    timeout: 70000
  });

  return mergeRevisionPlanResponse(validateRevisionPlanResponse(response.data), payload);
}

export async function generateLessonPlan(payload) {
  if (
    !payload.schoolName.trim() ||
    !payload.subject.trim() ||
    !payload.grade.trim() ||
    !payload.year.trim() ||
    !payload.term.trim() ||
    !payload.weekNumber.trim() ||
    !payload.dateRange.trim() ||
    !payload.mainTopic.trim()
  ) {
    throw new Error('School, subject, grade, year, term, week number, date range, and topic are required.');
  }

  if (!Array.isArray(payload.classes) || payload.classes.length === 0) {
    throw new Error('At least one class is required.');
  }

  if (!Number.isFinite(payload.lessonCount) || payload.lessonCount < 1 || payload.lessonCount > 8) {
    throw new Error('Lesson count must be between 1 and 8.');
  }

  const response = await axios.post(`${API_BASE}/ai/lesson-plan.php`, {
    ...payload,
    pageReference: typeof payload.pageReference === 'string' ? payload.pageReference.trim() : '',
    additionalContext: typeof payload.additionalContext === 'string' ? payload.additionalContext.trim() : '',
    classes: payload.classes.map((item) => item.trim()).filter(Boolean)
  }, {
    timeout: 70000
  });

  return validateLessonPlanResponse(response.data);
}
